import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import {
  AddEmployeesToApprovalGroupDocument,
  ApprovalsGroupFragment,
} from 'generated/resources/graphql'
import isNil from 'lodash/isNil'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import EmployeeSelector from 'modules/core/components/EmployeeSelector'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import approvalGroupMemberships from 'modules/approvals/store/collections/approvalGroupMembership'
import approvalGroups from 'modules/approvals/store/collections/approvalGroups'
import type ApprovalGroup from 'modules/approvals/store/models/ApprovalGroup'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'

interface Props {
  onClose: () => void
  approvalGroupId: number
  contractVersions: ContractVersions
}

const AddEmployeesModal = ({ onClose, approvalGroupId, contractVersions }: Props) => {
  const { group } = useOutletContext<{ group: ApprovalsGroupFragment }>()
  const { loading } = useAsyncData(async () => {
    await Promise.all([
      approvalGroups.fetch({ data: { category: group.category } }),
      approvalGroupMemberships.fetch({ data: { category: group.category } }),
    ])
  }, [group.category])

  const [approvalGroup, setApprovalGroup] = React.useState<ApprovalGroup | undefined>(undefined)
  const [selectedEmployeeIds, setSelectedEmployeeIds] = React.useState<number[]>([])
  const [unassignedEmployees, setUnassignedEmployees] = React.useState<Employee[]>([])

  const [addEmployeesToGroup, { loading: running }] = useMutation(
    AddEmployeesToApprovalGroupDocument,
    {
      extractorFn: (data) => data.approvalsMutations.bulkAssignApprovalsApprovalGroupMembership,
      refetchQueries: ['ApprovalGroupEmployeesDataTable'],
    }
  )

  const handleSubmit = async () => {
    if (!selectedEmployeeIds.length || !approvalGroup) return

    const accessIds = selectedEmployeeIds
      .map((employeeId) => {
        const employee = employees.get(employeeId)
        if (!employee) return null
        return employee.attributes.access_id
      })
      .filter(Boolean)

    const anyEmpty = accessIds.some((accessId) => !accessId)

    if (anyEmpty) throw 'There are some undefined accessIds'

    const result = await addEmployeesToGroup({
      variables: {
        accessIds: accessIds,
        approvalGroupId: approvalGroup.id,
        employeeIds: selectedEmployeeIds,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      onClose()
    }

    return handled
  }

  const handleSelectEmployee = useUnsafeCallback(
    (employeeId?: number | null) => {
      if (isNil(employeeId)) {
        setSelectedEmployeeIds([])

        return
      }

      setSelectedEmployeeIds(xor(selectedEmployeeIds, [employeeId]))
    },
    [selectedEmployeeIds, setSelectedEmployeeIds]
  )

  useUnsafeEffect(() => {
    if (loading) return

    setApprovalGroup(approvalGroups.get(approvalGroupId))
  }, [approvalGroupId, loading])

  useUnsafeEffect(() => {
    if (loading || !approvalGroup) return

    const memberships = approvalGroupMemberships.filterByApprovalGroup(approvalGroup)
    const assignedEmployeeIds = new Set(
      memberships.map((membership) => {
        return membership.attributes.access_id
      })
    )

    setUnassignedEmployees(
      employees.active.filter((e) => !assignedEmployeeIds.has(e.attributes.access_id))
    )
  }, [loading, approvalGroup])

  if (loading || !approvalGroup) {
    return (
      <Box alignItems="center" justifyContent="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <Modal
      title={i18n.t('settings.approvals.approval_group.employees.add_employees.title_modal')}
      description={i18n.t('settings.approvals.approval_group.employees.add_employees.description')}
      illustration="add-block"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Box alignItems="flexEnd" flexDirection="row" justifyContent="spaceBetween">
          <Button type="primary" disabled={selectedEmployeeIds.length === 0 || running} submit>
            {i18n.t('settings.approvals.approval_group.employees.add_employees.title')}
          </Button>
        </Box>
      }
    >
      <Box gap="s24">
        <EmployeeSelector
          employees={unassignedEmployees}
          selectedEmployeeIds={selectedEmployeeIds}
          onSelectEmployee={handleSelectEmployee}
          onSelectMultiple={setSelectedEmployeeIds}
          contractVersions={contractVersions}
        />
        <Box background="champagne400" padding="s16" borderRadius={{ all: 'abs008' }}>
          <Text.Normal>
            {i18n.t('settings.approvals.approval_group.employees.add_employees.warning')}
          </Text.Normal>
        </Box>
      </Box>
    </Modal>
  )
}
export default observer(AddEmployeesModal)
